import { useState, FormEvent, ChangeEvent } from 'react';
import { Link } from 'react-router-dom';

export default function Login() {
  const [form, setForm]           = useState({ email: '', password: '' });
  const [showPass, setShowPass]   = useState(false);
  const [errors, setErrors]       = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);

  const handleChange = (e: ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) setErrors((prev) => { const n = { ...prev }; delete n[name]; return n; });
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!form.email.trim() || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email))
      errs.email = 'A valid email is required';
    if (!form.password) errs.password = 'Password is required';
    return errs;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    const errs = validate();
    if (Object.keys(errs).length) { setErrors(errs); return; }
    setSubmitting(true);
    /* ── Auth will be wired to Supabase once connected ── */
    await new Promise((r) => setTimeout(r, 1000));
    localStorage.setItem('fr_member', 'true');
    setSubmitting(false);
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <div className="min-h-screen bg-navy flex items-center justify-center px-6">
        <div className="max-w-md w-full text-center">
          <div className="w-20 h-20 flex items-center justify-center rounded-full bg-gold/20 border-2 border-gold mx-auto mb-6">
            <i className="ri-check-double-line text-gold text-4xl" />
          </div>
          <h2 className="font-playfair text-3xl font-bold text-white mb-3">Welcome back!</h2>
          <p className="text-white/50 text-sm mb-8">
            You&apos;re signed in as <span className="text-white/80">{form.email}</span>
          </p>
          <Link
            to="/dashboard"
            className="inline-flex items-center gap-2 px-8 py-4 bg-gold text-navy-dark font-bold rounded-xl hover:bg-gold-light transition-colors whitespace-nowrap"
          >
            Go to Dashboard <i className="ri-arrow-right-line" />
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-navy flex flex-col lg:flex-row">

      {/* ── LEFT PANEL ── */}
      <div className="hidden lg:flex lg:w-[42%] flex-col justify-between p-12 xl:p-16 relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://public.readdy.ai/ai/img_res/ce6993932387f046b96693ba35d015c1.jpg"
            alt=""
            className="w-full h-full object-cover object-center opacity-20"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-navy/80 via-navy/60 to-navy/90" />
        </div>

        <div className="relative z-10 flex flex-col h-full justify-between">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
              alt="Foreclosure Recovery"
              className="h-14 w-auto object-contain"
            />
          </Link>

          <div>
            <span className="inline-block px-3 py-1 border border-gold/40 text-gold text-xs font-semibold tracking-widest uppercase rounded-full mb-6">
              Member Portal
            </span>
            <h2 className="font-playfair text-4xl xl:text-5xl font-bold text-white leading-tight mb-5">
              Your Next Deal<br />
              <span className="text-gold">Is Waiting</span>
            </h2>
            <p className="text-white/55 text-base leading-relaxed max-w-sm">
              Sign in to access your property pipeline, deal alerts, and the full Southwest foreclosure database.
            </p>

            <div className="grid grid-cols-3 gap-5 mt-10">
              {[
                { icon: 'ri-home-4-line', value: '2,847', label: 'Active Listings' },
                { icon: 'ri-percent-line', value: '94%', label: 'Success Rate' },
                { icon: 'ri-group-line', value: '500+', label: 'Members' },
              ].map((s) => (
                <div key={s.label} className="text-center">
                  <div className="w-10 h-10 flex items-center justify-center rounded-full bg-gold/10 border border-gold/20 mx-auto mb-2">
                    <i className={`${s.icon} text-gold text-base`} />
                  </div>
                  <p className="font-playfair text-2xl font-bold text-gold">{s.value}</p>
                  <p className="text-white/35 text-xs mt-0.5">{s.label}</p>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white/5 border border-white/10 rounded-2xl p-5 backdrop-blur-sm">
            <div className="flex items-start gap-3">
              <i className="ri-shield-check-line text-gold text-2xl flex-shrink-0 mt-0.5" />
              <div>
                <p className="text-white/80 text-sm font-semibold mb-1">Bank-level security</p>
                <p className="text-white/40 text-xs leading-relaxed">
                  Your account and data are protected with enterprise-grade encryption and two-factor authentication.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* ── RIGHT PANEL — form ── */}
      <div className="flex-1 flex flex-col justify-center px-6 py-12 md:px-12 xl:px-16">

        {/* Mobile logo */}
        <div className="lg:hidden mb-8 flex justify-center">
          <Link to="/">
            <img
              src="https://public.readdy.ai/ai/img_res/e4baf86f-3d4f-4e4d-945f-ebc375a864af.png"
              alt="Foreclosure Recovery"
              className="h-12 w-auto object-contain"
            />
          </Link>
        </div>

        <div className="max-w-[440px] w-full mx-auto">
          <h1 className="font-playfair text-3xl md:text-4xl font-bold text-white mb-1">
            Welcome Back
          </h1>
          <p className="text-white/45 text-sm mb-10">
            Sign in to access your member dashboard
          </p>

          <form onSubmit={handleSubmit} className="flex flex-col gap-5" noValidate>

            {/* Email */}
            <div>
              <label className="block text-xs font-semibold text-white/50 uppercase tracking-wide mb-2">
                Email Address
              </label>
              <div className="relative">
                <i className="ri-mail-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                <input
                  type="email"
                  name="email"
                  value={form.email}
                  onChange={handleChange}
                  placeholder="john@example.com"
                  autoComplete="email"
                  className={`w-full pl-10 pr-4 py-3.5 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                    errors.email ? 'border-red-500' : 'border-white/15 hover:border-white/30'
                  }`}
                />
              </div>
              {errors.email && <p className="text-red-400 text-xs mt-1">{errors.email}</p>}
            </div>

            {/* Password */}
            <div>
              <div className="flex items-center justify-between mb-2">
                <label className="text-xs font-semibold text-white/50 uppercase tracking-wide">
                  Password
                </label>
                <Link to="/forgot-password" className="text-xs text-gold/70 hover:text-gold transition-colors">
                  Forgot password?
                </Link>
              </div>
              <div className="relative">
                <i className="ri-lock-line absolute left-4 top-1/2 -translate-y-1/2 text-white/30 text-sm" />
                <input
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  value={form.password}
                  onChange={handleChange}
                  placeholder="Your password"
                  autoComplete="current-password"
                  className={`w-full pl-10 pr-11 py-3.5 rounded-xl bg-white/5 border text-white text-sm placeholder-white/20 focus:outline-none focus:border-gold transition-colors ${
                    errors.password ? 'border-red-500' : 'border-white/15 hover:border-white/30'
                  }`}
                />
                <button
                  type="button"
                  onClick={() => setShowPass((v) => !v)}
                  className="absolute right-4 top-1/2 -translate-y-1/2 text-white/30 hover:text-white/70 cursor-pointer transition-colors"
                >
                  <i className={`ri-eye${showPass ? '-off' : ''}-line text-sm`} />
                </button>
              </div>
              {errors.password && <p className="text-red-400 text-xs mt-1">{errors.password}</p>}
            </div>

            {/* Submit */}
            <button
              type="submit"
              disabled={submitting}
              className="w-full flex items-center justify-center gap-3 py-4 bg-gold text-navy-dark font-bold text-base rounded-xl hover:bg-gold-light transition-all duration-200 disabled:opacity-60 whitespace-nowrap cursor-pointer mt-2"
            >
              {submitting ? (
                <>
                  <i className="ri-loader-4-line animate-spin text-lg" />
                  Signing In…
                </>
              ) : (
                <>
                  <i className="ri-login-box-line text-lg" />
                  Sign In
                </>
              )}
            </button>
          </form>

          {/* Divider */}
          <div className="flex items-center gap-4 my-7">
            <div className="flex-1 h-px bg-white/10" />
            <span className="text-white/25 text-xs uppercase tracking-wide">or</span>
            <div className="flex-1 h-px bg-white/10" />
          </div>

          {/* Sign up link */}
          <div className="text-center space-y-3">
            <p className="text-white/40 text-sm">
              Don&apos;t have an account yet?
            </p>
            <Link
              to="/signup"
              className="flex items-center justify-center gap-2 w-full py-3.5 border border-white/20 text-white/70 font-semibold text-sm rounded-xl hover:border-gold/50 hover:text-gold transition-all duration-200 whitespace-nowrap"
            >
              <i className="ri-user-add-line" />
              Create a Free Account
            </Link>
            <Link
              to="/pricing"
              className="block text-white/25 text-xs hover:text-white/50 transition-colors"
            >
              View plan options →
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
}
